import { FC, Fragment, useState } from 'react';
import { FormControl, Select, MenuItem, Typography, FormHelperText } from '@mui/material';
import { FontGroup, getVariantLabel, getVariantHint } from '../utils/fontUtils';
import { StretchGroupHeader } from './StretchGroupHeader';

interface Props {
  currentWeight: number;
  currentStyle: string;
  currentStretch: string;
  fontGroup: FontGroup | undefined;
  missingVariant?: boolean;
  missingVariantLabel?: string;
  disabled: boolean;
  onChange: (next: { weight: number; style: string; stretch: string; label: string }) => void;
}

export const FontVariantSelect: FC<Props> = ({
  currentWeight,
  currentStyle,
  currentStretch,
  fontGroup,
  missingVariant = false,
  missingVariantLabel = '',
  disabled,
  onChange,
}) => {
  const currentValue = `${currentWeight}||${currentStyle}||${currentStretch}`;
  const [open, setOpen] = useState(false);

  // Flatten all variants for lookup
  const allVariants = fontGroup?.stretchGroups.flatMap((sg) => sg.variants) || [];

  return (
    <FormControl fullWidth error={missingVariant}>
      <Select
        size="small"
        aria-label="Font variant"
        value={currentValue}
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        MenuProps={{
          autoFocus: false,
        }}
        renderValue={(selected) => {
          const [weightStr, style, stretch] = (selected as string).split('||');
          const weight = parseInt(weightStr, 10);

          if (isNaN(weight)) {
            return getVariantLabel(currentWeight, currentStyle);
          }

          const variant = allVariants.find(
            (v) => v.weight === weight && v.style === style && v.stretch === stretch,
          );
          if (variant) {
            return variant.label;
          }

          const fallbackLabel = getVariantLabel(weight, style);
          const label = missingVariant && missingVariantLabel ? missingVariantLabel : fallbackLabel;
          return missingVariant ? `${label} (Missing)` : label;
        }}
      >
        {fontGroup?.stretchGroups.map((stretchGroup) => {
          const shouldShowHeader =
            stretchGroup.stretch !== 'normal' || fontGroup.stretchGroups.length > 1;
          const shouldIndent = shouldShowHeader;

          return (
            <Fragment key={stretchGroup.stretch}>
              <StretchGroupHeader label={stretchGroup.stretchLabel} shouldShow={shouldShowHeader} />
              {stretchGroup.variants.map((variant) => {
                const generatedHint = getVariantHint(variant.weight, variant.style);
                const hint =
                  variant.variant && variant.variant !== generatedHint ? ` (${generatedHint})` : '';
                const variantValue = `${variant.weight}||${variant.style}||${variant.stretch}`;
                return (
                  <MenuItem
                    key={variantValue}
                    value={variantValue}
                    onClick={() => {
                      onChange({
                        weight: variant.weight,
                        style: variant.style,
                        stretch: variant.stretch,
                        label: variant.label,
                      });
                      setOpen(false);
                    }}
                    sx={{
                      pl: shouldIndent ? 4 : 2,
                    }}
                  >
                    {variant.label}
                    {hint && (
                      <Typography
                        component="span"
                        color="text.secondary"
                        sx={{ ml: 1, fontSize: '0.875em' }}
                      >
                        {hint}
                      </Typography>
                    )}
                  </MenuItem>
                );
              })}
            </Fragment>
          );
        })}
      </Select>
      {missingVariant && (
        <FormHelperText>
          {missingVariantLabel
            ? `Selected variant "${missingVariantLabel}" is not available for this font.`
            : 'Selected variant is not available for this font.'}
        </FormHelperText>
      )}
    </FormControl>
  );
};
